import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

const IDLE_TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes
/** Must match the `duration-400` on the exit transition, or the node is dropped mid-fade. */
const EXIT_FADE_MS = 400
const TRIANGLE_COUNT = 70

/**
 * Idle screensaver — "Lift"
 *
 * The defining mark of the FreighAI logo is a small upward-pointing purple
 * triangle. This screensaver scales that mark into ambient motion: dozens of
 * triangles drift slowly upward across the canvas at varying sizes, depths,
 * and opacities. The logo (which already contains the container squares in
 * the "f" and "i") sits at the center as the anchor. Calm, branded, distinctly
 * FreighAI — visual rhyme between the brand and its ambience.
 */

interface Triangle {
  id: number
  size: number
  startX: number // 0..100 (% of viewport width)
  duration: number // s
  delay: number // s (negative for mid-flight on mount)
  opacity: number
  layer: 'fore' | 'mid' | 'back'
  filled: boolean
  hue: 'primary' | 'light'
}

function seededRandom(seed: number): number {
  const x = Math.sin(seed * 9999.17) * 10000
  return x - Math.floor(x)
}

function generateTriangles(): Triangle[] {
  return Array.from({ length: TRIANGLE_COUNT }, (_, i) => {
    const seed = i + 1
    const r1 = seededRandom(seed)
    const r2 = seededRandom(seed * 3)
    const r3 = seededRandom(seed * 5)
    const r4 = seededRandom(seed * 7)
    const r5 = seededRandom(seed * 11)
    const r6 = seededRandom(seed * 13)

    let layer: Triangle['layer'] = 'mid'
    let size = 0
    let duration = 0
    let opacity = 0

    if (r5 < 0.55) {
      layer = 'back'
      size = 4 + r1 * 5 // 4–9 px
      duration = 80 + r3 * 50
      opacity = 0.1 + r2 * 0.15
    } else if (r5 < 0.88) {
      layer = 'mid'
      size = 8 + r1 * 6 // 8–14 px
      duration = 50 + r3 * 35
      opacity = 0.25 + r2 * 0.2
    } else {
      layer = 'fore'
      size = 14 + r1 * 12 // 14–26 px
      duration = 30 + r3 * 25
      opacity = 0.5 + r2 * 0.35
    }

    return {
      id: i,
      size,
      startX: r4 * 100,
      duration,
      delay: -r6 * 100,
      opacity,
      layer,
      filled: r1 > 0.25,
      hue: r6 > 0.7 ? 'light' : 'primary',
    }
  })
}

const COLOR_PRIMARY = '#A259FF'
const COLOR_LIGHT = '#C792FF'

export function IdleScreensaver() {
  /**
   * One three-state machine instead of a mounted/shown pair.
   *
   *   off      — not in the tree at all
   *   on       — visible; fades IN with a CSS animation, so no second state is needed for it
   *   leaving  — still in the tree at opacity 0 while the exit transition runs, then dropped
   *
   * **The overlay used to be mounted permanently, with only its opacity toggled.** That is not
   * free: 70 triangles and the haze each carry an `infinite` CSS animation, so 71 compositor
   * animations ran on every page, for the whole session, behind a fully transparent box. It was
   * heavy enough to stall screenshot capture outright. `off` removes them rather than hiding
   * them.
   *
   * Every transition is driven from a timer or an event handler — never from an effect body,
   * which is both what `react-hooks/set-state-in-effect` asks for and the honest description of
   * what is happening: this is a clock and some input listeners, not state being synchronised.
   */
  const [phase, setPhase] = useState<'off' | 'on' | 'leaving'>('off')
  const shown = phase === 'on'
  const timerRef = useRef<number | null>(null)
  const exitRef = useRef<number | null>(null)
  const triangles = useMemo(() => generateTriangles(), [])

  useEffect(() => {
    const resetIdle = () => {
      // Begin the exit only if something is actually showing. Calling this on every mousemove is
      // the common case, and it must not schedule work when the answer is already "off".
      setPhase((p) => {
        if (p === 'off') return 'off'
        if (exitRef.current === null) {
          exitRef.current = window.setTimeout(() => {
            exitRef.current = null
            setPhase('off')
          }, EXIT_FADE_MS)
        }
        return 'leaving'
      })
      if (timerRef.current !== null) window.clearTimeout(timerRef.current)
      timerRef.current = window.setTimeout(() => {
        if (exitRef.current !== null) {
          window.clearTimeout(exitRef.current)
          exitRef.current = null
        }
        setPhase('on')
      }, IDLE_TIMEOUT_MS)
    }

    const events: (keyof WindowEventMap)[] = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'wheel',
    ]
    events.forEach((e) => window.addEventListener(e, resetIdle, { passive: true }))
    resetIdle()

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetIdle))
      if (timerRef.current !== null) window.clearTimeout(timerRef.current)
      if (exitRef.current !== null) window.clearTimeout(exitRef.current)
    }
  }, [])

  // Nothing in the tree while the user is active — which is nearly always. This early return is
  // the whole fix: an invisible animation is still an animation.
  if (phase === 'off') return null

  return (
    <div
      aria-hidden={!shown}
      role="presentation"
      className={cn(
        'fixed inset-0 z-[9999] overflow-hidden bg-background',
        shown
          // Entering: a CSS animation, not a transition. A transition needs a painted "from"
          // value, which on a freshly mounted node means an extra state flip a frame later —
          // exactly the setState-in-effect this component no longer has.
          ? 'pointer-events-auto animate-in fade-in duration-700'
          : 'pointer-events-none opacity-0 transition-opacity duration-400 ease-out',
      )}
    >
      {/* Vertical gradient — denser purple at the bottom, fading up */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'linear-gradient(to top, rgba(162,89,255,0.14) 0%, rgba(162,89,255,0.04) 35%, transparent 70%)',
        }}
      />

      {/* Soft central haze around the logo */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-1/2 h-[900px] w-[900px] -translate-x-1/2 -translate-y-1/2 saver-haze">
          <div className="h-full w-full rounded-full bg-primary/10 blur-3xl" />
        </div>
      </div>

      {/* Rising triangles */}
      <div className="pointer-events-none absolute inset-0">
        {triangles.map((t) => {
          const color = t.hue === 'primary' ? COLOR_PRIMARY : COLOR_LIGHT
          const height = t.size * 0.866 // equilateral
          const baseStyle: React.CSSProperties = {
            left: `${t.startX}%`,
            top: 0,
            animationDuration: `${t.duration}s`,
            animationDelay: `${t.delay}s`,
            opacity: shown ? t.opacity : 0,
            transition: 'opacity 1500ms ease-out',
            willChange: 'transform',
          }
          const triangle = t.filled ? (
            <div
              style={{
                width: 0,
                height: 0,
                borderLeft: `${t.size / 2}px solid transparent`,
                borderRight: `${t.size / 2}px solid transparent`,
                borderBottom: `${height}px solid ${color}`,
                filter:
                  t.layer === 'fore'
                    ? `drop-shadow(0 0 ${t.size * 0.5}px rgba(162, 89, 255, 0.55))`
                    : undefined,
              }}
            />
          ) : (
            <svg width={t.size} height={height} viewBox={`0 0 ${t.size} ${height}`}>
              <polygon
                points={`${t.size / 2},0 0,${height} ${t.size},${height}`}
                fill="none"
                stroke={color}
                strokeWidth={t.layer === 'fore' ? 1.5 : 1}
                strokeLinejoin="round"
              />
            </svg>
          )
          return (
            <div key={t.id} className="absolute saver-rise" style={baseStyle}>
              {triangle}
            </div>
          )
        })}
      </div>

      {/* Hero logo + tagline */}
      <div className="relative flex h-full flex-col items-center justify-center">
        <div
          className={cn(
            'transition-transform duration-700 ease-out',
            shown ? 'scale-100 animate-logo-breathe' : 'scale-95',
          )}
        >
          <img
            src="/logo-wordmark-dark.png"
            alt=""
            className="block h-32 w-auto object-contain dark:hidden"
          />
          <img
            src="/logo-wordmark-light.png"
            alt=""
            className="hidden h-32 w-auto object-contain dark:block"
          />
        </div>
        <p
          className={cn(
            'mt-8 font-mono text-[11px] uppercase tracking-[0.4em] text-primary/70',
            'transition-opacity duration-1000',
            shown ? 'opacity-100 delay-700' : 'opacity-0',
          )}
        >
          Freight, lifted by AI
        </p>
      </div>

      <style>{`
        @keyframes saver-rise {
          0%   { transform: translateY(105vh); }
          100% { transform: translateY(-12vh); }
        }
        .saver-rise { animation: saver-rise linear infinite; }

        @keyframes saver-haze {
          0%, 100% { transform: translate3d(-4vw, -3vh, 0) scale(1); }
          50%      { transform: translate3d(4vw, 3vh, 0) scale(1.04); }
        }
        .saver-haze { animation: saver-haze 60s ease-in-out infinite; }

        @media (prefers-reduced-motion: reduce) {
          .saver-rise, .saver-haze { animation: none !important; }
        }
      `}</style>
    </div>
  )
}
